import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { createSchedulingRuleSchema, EMPTY_SCHEDULING_RULE, SchedulingRuleFormValues } from 'schemas/SchedulingRule';
import { useSchedulingActions } from './useSchedulingActions';
import type { Gravity, GravityRule, SchedulingRule, SchedulingRuleBase } from 'repository/schedule/@types/SchedulingRuleModel';
import type { ShiftProfileGroup } from 'repository/schedule/@types/ShiftProfileGroup';
import { ScheduleService } from 'services/ScheduleService';

interface Props {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: SchedulingRule[];
    selectedRow: SchedulingRule[];
    reloading: () => void;
    mode: 'add' | 'edit';
    cleanSelected: () => void;
}

const SR = 'scheduling_rules.scheduling_rule';
const scheduleService = new ScheduleService();

const WEEKDAY_TO_NUMBER: Record<string, number> = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 7,
};

const NUMBER_TO_WEEKDAY: Record<number, SchedulingRuleFormValues['weekStartDay']> = {
    1: 'monday',
    2: 'tuesday',
    3: 'wednesday',
    4: 'thursday',
    5: 'friday',
    6: 'saturday',
    7: 'sunday',
};

const GRAVITY_MIN = 0;
const GRAVITY_MAX = 24 * 7;

const EMPTY_GRAVITY_RULE: GravityRule = { gravity: 'not_considered', value: 0 };

const clampGravityValue = (value: number): number => {
    if (value < GRAVITY_MIN) return GRAVITY_MIN;
    if (value > GRAVITY_MAX) return GRAVITY_MAX;
    return value;
};

const toSingleDecimal = (value: number): number => Math.round(value * 10) / 10;
const toInteger = (value: number): number => Math.trunc(value);

const extractTimeHHMM = (value?: string | null): string => {
    if (!value) return '00:00';
    const match = String(value).match(/(\d{2}):(\d{2})/);
    if (!match) return '00:00';
    return `${match[1]}:${match[2]}`;
};

const toApiTime = (value: string): string => {
    if (/^\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/.test(value)) {
        return value;
    }
    if (/^\d{2}:\d{2}$/.test(value)) {
        return `${value}:00`;
    }
    return '00:00:00';
};

const hoursFromTime = (value: string): number => {
    const [hours, minutes] = value.split(':').map((part) => Number(part));
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0;
    return toSingleDecimal(hours + minutes / 60);
};

const timeFromHours = (value?: number | string | null): string => {
    if (value === null || value === undefined) return '00:00';
    const numericValue = typeof value === 'string' ? Number(value) : value;
    if (!Number.isFinite(numericValue)) return '00:00';
    const boundedHours = Math.max(0, Math.min(24, numericValue));
    const totalMinutes = Math.round(boundedHours * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const toUiGravity = (severity?: string): Gravity => {
    const normalized = String(severity || '').toLowerCase();
    if (normalized === 'warning') return 'warning';
    if (normalized === 'no safeguard') return 'no_safeguard';
    return 'not_considered';
};

const toApiSeverity = (gravity: Gravity): string => {
    if (gravity === 'warning') return 'warning';
    if (gravity === 'no_safeguard') return 'no safeguard';
    return 'not considered';
};

const normalizeGravityRuleFromApi = (value: { severity?: string; value: number } | undefined, valueType: 'float' | 'int'): GravityRule => {
    if (!value) return EMPTY_GRAVITY_RULE;
    const boundedValue = value.value === null || value.value === undefined ? 0 : clampGravityValue(value.value);
    const numericValue = valueType === 'float' ? toSingleDecimal(boundedValue) : toInteger(boundedValue);
    return {
        gravity: toUiGravity(value.severity),
        value: numericValue,
    };
};

const toApiGravityRule = (rule: GravityRule, defaultValue: number, valueType: 'float' | 'int'): { severity: string; value: number } => {
    if (!rule.gravity) {
        return {
            severity: 'not considered',
            value: defaultValue,
        };
    }

    const boundedValue = clampGravityValue(rule.value);
    const normalizedValue = valueType === 'float' ? toSingleDecimal(boundedValue) : toInteger(boundedValue);

    return {
        severity: toApiSeverity(rule.gravity),
        value: normalizedValue,
    };
};

const toFormFromSchedulingRule = (rule: SchedulingRule): SchedulingRuleFormValues => ({
    name: rule.name,
    description: rule.description ?? '',
    weekStartDay: NUMBER_TO_WEEKDAY[rule.day_week_begins] ?? 'monday',
    nightStartTime: extractTimeHHMM(rule.night_shift_start_time),
    nightEndTime: extractTimeHHMM(rule.night_shift_end_time),
    nightLimitTime: timeFromHours(rule.threshold_night_definition),
    maxSegmentsPerShift: Math.max(0, Number(rule.max_shift_segments_per_shifts?.value ?? 0)),
    granularity: String(rule.granularity ?? 15),
    borderGranularity: String(rule.boundary_granularity ?? 15),
    shiftProfileGroupId: rule.shift_profile_group_id ?? null,
    considerPreviousPeriod: Boolean(rule.lookback),
    minDailyWorkHours: normalizeGravityRuleFromApi(rule.min_daily_hours, 'float'),
    maxDailyWorkHours: normalizeGravityRuleFromApi(rule.max_daily_hours, 'float'),
    minPeriodWorkHours: normalizeGravityRuleFromApi(rule.min_period_hours, 'float'),
    maxPeriodWorkHours: normalizeGravityRuleFromApi(rule.max_period_hours, 'float'),
    minHoursBetweenShifts: normalizeGravityRuleFromApi(rule.min_hours_between_shifts, 'float'),
    minWorkDays: normalizeGravityRuleFromApi(rule.min_days_worked, 'int'),
    maxWorkDays: normalizeGravityRuleFromApi(rule.max_days_worked, 'int'),
    minConsecutiveWorkDays: normalizeGravityRuleFromApi(rule.min_consecutive_work_days, 'int'),
    maxConsecutiveWorkDays: normalizeGravityRuleFromApi(rule.max_consecutive_work_days, 'int'),
    minConsecutiveDaysOff: normalizeGravityRuleFromApi(rule.min_consecutive_days_off, 'int'),
    maxConsecutiveDaysOff: normalizeGravityRuleFromApi(rule.max_consecutive_days_off, 'int'),
    minDaysBetweenNightShifts: normalizeGravityRuleFromApi(rule.min_days_between_nightshifts, 'int'),
    minDaysBetweenRestDays: normalizeGravityRuleFromApi(rule.min_days_between_days_off_groups, 'int'),
    minNightShifts: normalizeGravityRuleFromApi(rule.min_night_shifts, 'int'),
    maxNightShifts: normalizeGravityRuleFromApi(rule.max_night_shifts, 'int'),
    minConsecutiveNightShifts: normalizeGravityRuleFromApi(rule.min_consecutive_night_shifts, 'int'),
    maxConsecutiveNightShifts: normalizeGravityRuleFromApi(rule.max_consecutive_night_shifts, 'int'),
    minDaysBetweenGroups: normalizeGravityRuleFromApi(rule.min_days_between_night_groups, 'int'),
});

const toSchedulingRuleBody = (form: SchedulingRuleFormValues, currentRule?: SchedulingRule): SchedulingRuleBase => {
    const shiftProfileGroupId = form.shiftProfileGroupId ?? null;

    return {
        shift_profile_group_id: shiftProfileGroupId,
        name: form.name,
        description: form.description,
        day_week_begins: WEEKDAY_TO_NUMBER[form.weekStartDay] ?? 1,
        granularity: Number(form.granularity) || 15,
        threshold_night_definition: hoursFromTime(form.nightLimitTime),
        lookback: form.considerPreviousPeriod,
        boundary_granularity: Number(form.borderGranularity) || 15,
        min_daily_hours: toApiGravityRule(form.minDailyWorkHours, 0, 'float'),
        max_daily_hours: toApiGravityRule(form.maxDailyWorkHours, 0, 'float'),
        min_period_hours: toApiGravityRule(form.minPeriodWorkHours, 0, 'float'),
        max_period_hours: toApiGravityRule(form.maxPeriodWorkHours, 0, 'float'),
        min_days_worked: toApiGravityRule(form.minWorkDays, 0, 'int'),
        max_days_worked: toApiGravityRule(form.maxWorkDays, 0, 'int'),
        min_days_worked_per_week: { severity: 'not considered', value: 0 },
        max_days_worked_per_week: { severity: 'not considered', value: 0 },
        min_weekly_hours: { severity: 'not considered', value: 0 },
        max_weekly_hours: { severity: 'not considered', value: 0 },
        min_hours_between_shifts: toApiGravityRule(form.minHoursBetweenShifts, 0, 'float'),
        min_consecutive_work_days: toApiGravityRule(form.minConsecutiveWorkDays, 0, 'int'),
        max_consecutive_work_days: toApiGravityRule(form.maxConsecutiveWorkDays, 0, 'int'),
        min_consecutive_days_off: toApiGravityRule(form.minConsecutiveDaysOff, 0, 'int'),
        max_consecutive_days_off: toApiGravityRule(form.maxConsecutiveDaysOff, 0, 'int'),
        min_days_between_nightshifts: toApiGravityRule(form.minDaysBetweenNightShifts, 0, 'int'),
        min_night_shifts: toApiGravityRule(form.minNightShifts, 0, 'int'),
        max_night_shifts: toApiGravityRule(form.maxNightShifts, 0, 'int'),
        min_night_per_week: { severity: 'not considered', value: 0 },
        max_night_per_week: { severity: 'not considered', value: 0 },
        min_night_per_month: { severity: 'not considered', value: 0 },
        max_night_per_month: { severity: 'not considered', value: 0 },
        min_consecutive_night_shifts: toApiGravityRule(form.minConsecutiveNightShifts, 0, 'int'),
        max_consecutive_night_shifts: toApiGravityRule(form.maxConsecutiveNightShifts, 0, 'int'),
        max_weekly_shift_variety: { severity: 'not considered', value: 0 },
        max_shift_segments_per_shifts: {
            severity: 'not considered',
            value: form.maxSegmentsPerShift > 0 ? form.maxSegmentsPerShift : 0,
        },
        night_shift_start_time: toApiTime(form.nightStartTime),
        night_shift_end_time: toApiTime(form.nightEndTime),
        min_days_between_night_groups: toApiGravityRule(form.minDaysBetweenGroups, 0, 'int'),
        min_days_between_days_off_groups: toApiGravityRule(form.minDaysBetweenRestDays, 0, 'int'),
        has_breaks: false,
        min_hours_for_break: { severity: 'not considered', value: 0 },
        break_duration: { severity: 'not considered', value: 0 },
        min_percentage_from_start: { severity: 'not considered', value: 0 },
        min_percentage_from_end: { severity: 'not considered', value: 0 },
        static_shift_patterns: false,
        forbidden_patterns: null,
    };
};

export const useSchedulingRuleForm = ({ open, setOpen, rows, selectedRow, reloading, mode, cleanSelected }: Props) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { addSchedulingRule, updateSchedulingRule } = useSchedulingActions();

    const [activeStep, setActiveStep] = useState(0);
    const [incompleteWarning, setIncompleteWarning] = useState(false);
    const [shiftProfileGroups, setShiftProfileGroups] = useState<ShiftProfileGroup[]>([]);
    const [loadingShiftProfileGroups, setLoadingShiftProfileGroups] = useState(false);

    const currentSchedulingRule = mode === 'edit' ? selectedRow[0] : undefined;

    const defaultValues: SchedulingRuleFormValues = useMemo(() => {
        if (mode === 'edit' && currentSchedulingRule) {
            return toFormFromSchedulingRule(currentSchedulingRule);
        }
        return EMPTY_SCHEDULING_RULE;
    }, [mode, currentSchedulingRule]);

    const schema = useMemo(() => createSchedulingRuleSchema({ t, rows, initialValues: mode === 'edit' ? { name: currentSchedulingRule?.name } : undefined }), [t, rows, mode, currentSchedulingRule]);

    type SchemaType = z.infer<ReturnType<typeof createSchedulingRuleSchema>>;

    const {
        control,
        reset,
        handleSubmit,
        formState: { errors, isSubmitting, isValid },
    } = useForm<SchemaType>({ defaultValues, resolver: zodResolver(schema as any), mode: 'onChange' });

    useEffect(() => {
        if (open) {
            reset(defaultValues);
            setActiveStep(0);
            setIncompleteWarning(false);
        }
    }, [open, reset, defaultValues]);

    useEffect(() => {
        if (!open) {
            return;
        }

        let mounted = true;

        const loadShiftProfileGroups = async () => {
            setLoadingShiftProfileGroups(true);
            try {
                const data = await scheduleService.shift_profile_group.getAll();
                if (mounted) {
                    setShiftProfileGroups(data);
                }
            } catch (error) {
                console.error('Error cargando grupos de perfil de turno:', error);
                if (mounted) {
                    setShiftProfileGroups([]);
                }
            } finally {
                if (mounted) {
                    setLoadingShiftProfileGroups(false);
                }
            }
        };

        void loadShiftProfileGroups();

        return () => {
            mounted = false;
        };
    }, [open]);

    const handleClose = () => {
        setOpen(false);
        cleanSelected();
    };

    const handleAlert = (message: { message: any; severity: 'error' | 'success' | 'warning' | 'info' }) => {
        enqueueSnackbar(message.message, { variant: message.severity });
    };

    const submitForm = async (values: SchemaType) => {
        const body = toSchedulingRuleBody(values, currentSchedulingRule);

        if (mode === 'add') {
            await addSchedulingRule({
                setLoading: () => {},
                body,
                closing: handleClose,
                reloading,
                messageError: {
                    title: 'Error',
                    message: t(`${SR}.dialog.alerts.add.error`),
                    severity: 'error',
                },
                messageSuccess: {
                    title: 'Success',
                    message: t(`${SR}.dialog.alerts.add.success`),
                    severity: 'success',
                },
                handlerAlert: handleAlert,
            });
            return;
        }

        if (mode === 'edit' && currentSchedulingRule && currentSchedulingRule.ruleset_id !== undefined) {
            await updateSchedulingRule({
                setLoading: () => {},
                schedulingRuleId: currentSchedulingRule.ruleset_id,
                body,
                closing: handleClose,
                reloading,
                messageError: {
                    title: 'Error',
                    message: t(`${SR}.dialog.alerts.edit.error`),
                    severity: 'error',
                },
                messageSuccess: {
                    title: 'Success',
                    message: t(`${SR}.dialog.alerts.edit.success`),
                    severity: 'success',
                },
                handlerAlert: handleAlert,
            });
        }
    };

    const handleFinalSubmit = handleSubmit((values) => {
        if (!isValid) {
            setIncompleteWarning(true);
            return;
        }
        void submitForm(values);
    });

    return {
        dialogTitle: mode === 'add' ? t(`${SR}.dialog.titles.add`) : t(`${SR}.dialog.titles.edit`),
        cancelLabel: t('general.buttons.close'),
        submitLabel: t('general.buttons.save'),
        control,
        errors,
        isSubmitting,
        activeStep,
        setActiveStep,
        incompleteWarning,
        setIncompleteWarning,
        shiftProfileGroups,
        loadingShiftProfileGroups,
        handleClose,
        handleFinalSubmit,
    };
};
